import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DateRangeInterface, DSSetup, Service, ValidationErrors } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import type { ClientSource } from 'webapp/components/client/metrics/edit';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricSetupForClient } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import MetricFromClient from 'webapp/models-custom/metric-from-client';
import MetricLimitations from 'webapp/models-custom/metric-limitations';

interface ClientData {
	sources: FilterOption<string>[];
	metrics: MetricFromClient[];
}

interface Args {
	data: ClientSource[] | null;
	errors: ValidationErrors<string>;
	isFromThisSpace: boolean;
	selectedDateRange: DateRangeInterface.FormulaInterface | null;
	submitInProgress: boolean;
	metricSetup: MetricSetupForClient;
	hideOptionToSelectDimensions?: boolean;
	onUpdateDateRange: (value: DateRangeInterface.FormulaInterface | null) => void;
	onRecenter: () => void;
	onResetErrors: () => void;
}

export default class ClientMetricSetupFormComponent extends Component<Args> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics')
	declare mmMetricsService: Service.ModelManager.Metrics;

	limitationsCM: MetricLimitations;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		let dateRange: DateRange;

		if (this.args.selectedDateRange) {
			dateRange = DateRange.createFromFormula(this.args.selectedDateRange);
		} else {
			const optimalDateRangeSelectionId = DateRange.getOptimalSelectionId();
			dateRange = DateRange.createFromRangeUISelectionId(optimalDateRangeSelectionId);
			next(() => {
				this.updateDateRange(dateRange);
			});
		}

		const metricData = this.metricData;
		if (metricData) {
			this.findAndSetClientMetricFromMetricData(metricData);
		}

		const limitations = new MetricLimitations({
			metrics: this.metricData ? [this.metricData] : [],
			isNumber: true,
			dateRange,
		});

		this.limitationsCM = limitations;
	}

	get metricSetup(): MetricSetupForClient {
		return this.args.metricSetup;
	}

	get metricData(): DSSetup.MetricData | null {
		return this.metricSetup.metricData ?? null;
	}

	get clientMetrics(): MetricFromClient[] {
		return this.clientData?.metrics ?? [];
	}

	get clientData(): ClientData | undefined {
		const data = this.args.data;

		if (!data) {
			return;
		}

		const clientData: ClientData = {
			sources: [],
			metrics: [],
		};

		(data || []).forEach((source) => {
			const sourceId = source.id,
				sourceAvatar = source.avatar;

			clientData.sources.push({
				id: String(sourceId),
				text: source.title,
				img: sourceAvatar,
			});

			source.metrics.forEach((metric) => {
				clientData.metrics.push(
					new MetricFromClient({
						id: metric.id!,
						key: metric.key,
						spaceAccessId: metric.spaceAccessId ?? sourceId, // for basic metrics BE does not explicitly return spaceAccessId so we set it from the returned source above
						dimensions: metric.dimensions,
						sourceAvatar,
						dataSourceTypeKey: metric.dataSourceTypeKey,
						objectType: metric.objectType,
						isStandalone: metric.isStandalone,
						name: metric.name,
						description: metric.description,
						sourceName: source.title,
						hasUnits: metric.hasUnits,
						status: metric.status,
					}),
				);
			});
		});

		return clientData ?? [];
	}

	@action
	changeMetricData(metricData: DSSetup.MetricData): void {
		// this has to be done in this order
		// because setFromMetricData resets the dimensions and filters
		this.metricSetup.setFromMetricData(metricData);
		this.findAndSetClientMetricFromMetricData(metricData);
		this.limitationsCM.changeMetrics([metricData]);
		this.setOptimalUnit();
		this.args.onResetErrors();
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.changeValue('dimensions', dimensions);
	}

	@action
	changeAttribute(value: string | null): void {
		this.metricSetup.setFiltersFromAttributeName(value);
		this.args.onResetErrors();
	}

	@action
	changeUnit(value: string | null): void {
		this.changeValue('unitName', value!);
	}

	@action
	updateDateRange(value: DateRange | null): void {
		this.args.onUpdateDateRange(value?.formula || null);
		if (value) {
			this.limitationsCM.updateDateRangeFromOnlyRangeFormula(value.rangeFormula);
		}
	}

	@action
	recenter(): void {
		this.args.onRecenter();
	}

	private findAndSetClientMetricFromMetricData(metricData: DSSetup.MetricData): void {
		const clientMetrics = this.clientData?.metrics ?? [],
			metricSetup = this.metricSetup;

		if (!clientMetrics) {
			return;
		}

		const data = metricData ?? metricSetup.metricData;
		const clientMetric = clientMetrics.find((metric) => metric.key === data.key);

		if (!clientMetric) {
			return;
		}

		metricSetup.setMetric(clientMetric);
	}

	private setOptimalUnit(): void {
		const metricSetup = this.metricSetup;
		if (!metricSetup.hasUnits || metricSetup.specificSelectedUnit) {
			return;
		}

		const metricData = metricSetup.metricData;
		if (!metricData) {
			return;
		}

		const prevKey = metricSetup.key,
			prevUnit = metricSetup.unitName;

		void this.mmMetricsService
			.getOptimalUnit(metricData)
			.then((unit) => {
				// check if user has not changed the metric inbetween
				if (prevKey !== metricSetup.key) {
					return;
				}

				if (prevUnit !== metricSetup.unitName) {
					return;
				}

				this.changeUnit(unit);
			})
			.catch();
	}

	private changeValue<K extends keyof MetricSetupForClient>(
		prop: K,
		value: MetricSetupForClient[K],
	): void {
		this.metricSetup[prop] = value;
		this.args.onResetErrors();
	}
	<template>
		<div
			{{didUpdate
				this.recenter
				this.metricSetup.hasUnits
				this.metricSetup.allowBasicDimensionValueFilters
			}}
		>
			<div class="row">
				<FormMetricSelectComponent
					@selected={{this.metricData}}
					@onChange={{this.changeMetricData}}
					@customClientMetrics={{this.clientData.metrics}}
					@filterSortSourceOptions={{this.clientData.sources}}
					@skipConnected={{@isFromThisSpace}}
					@skipCreateCustomMetrics={{true}}
					@skipCustomGenerics={{true}}
					@hideConnectBtn={{true}}
					@hideNoResultsActions={{true}}
					@isDisabled={{@submitInProgress}}
					@size="big"
					@hideOptionToSelectDimensions={{@hideOptionToSelectDimensions}}
				/>
				<RowErrorComponent @items={{@errors.selectedSource}} />
				<RowErrorComponent @items={{@errors.selectedMetric}} />
			</div>

			{{#unless @hideOptionToSelectDimensions}}
				{{#if this.metricSetup.allowDimensions}}
					<div class="row">
						<FormSelectDimensionsComponent
							@metricSetup={{this.metricSetup}}
							@onChange={{this.changeDimensions}}
						/>
					</div>
				{{/if}}

				{{#if this.metricSetup.allowBasicDimensionValueFilters}}
					<div class="row">
						<FormSelectAttributesComponent
							@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
							@metricSetupForQuery={{this.metricSetup}}
							@onChange={{this.changeAttribute}}
							@isDisabled={{@submitInProgress}}
							@isRequired={{true}}
						/>
						<RowErrorComponent @items={{@errors.selectedAttribute}} />
					</div>
				{{/if}}

				{{#if this.metricSetup.hasUnits}}
					<div class="row">
						<FormSelectUnitsComponent
							@selected={{this.metricSetup.selectedUnit}}
							@metricSetupForQuery={{this.metricSetup}}
							@onChange={{this.changeUnit}}
							@isDisabled={{@submitInProgress}}
							@isRequired={{true}}
							@allowAllUnits={{false}}
						/>
						<RowErrorComponent @items={{@errors.selectedUnit}} />
					</div>
				{{/if}}
			{{/unless}}
			<div class="row">
				<FormRangeDropdownComponent
					@metricLimitations={{this.limitationsCM}}
					@isDisabled={{@submitInProgress}}
					@onChange={{this.updateDateRange}}
				/>
				<RowErrorComponent @items={{@errors.dateRange}} />
			</div>

		</div>
	</template>
}
